import {
  AgendaDisponibilidade,
  AgendaDisponibilidadeAgenda,
  AgendaDisponibilidadeParams,
  AgendaDisponibilidadeUnidade,
  AgendaExame,
  AgendaGradeLinha,
  AgendaPlanoPaciente,
  AgendaPrecoPaciente,
  AgendaPrecoParams,
  AgendaPreparoExame,
  AgendaPrecos,
  AgendaPrecosParams,
  AgendaRetornoDisponivel,
  AgendaVaga,
} from '../../../../interfaces/agenda';
import { AgendaFiltroAplicado, somarDias } from '../agenda-filtro';
import { AgendaJanela } from '../agenda-transferir/agenda-transferir';
import { AgendaPacienteEscolhido } from '../agenda-paciente/agenda-paciente';

/** ODONTOLOGIA só na SiM; no back é a busca de consulta com área de atuação 1. */
export type AgendaTipoBusca = 'CONSULTA' | 'EXAME' | 'ODONTOLOGIA';

/** Mesmo horizonte do original (1 ano), mas pedido em janelas: de uma vez o teto de 3000 linhas chega em poucos dias. */
export const DIAS_HORIZONTE = 365;
export const JANELA_INICIAL_DIAS = 14;
/** Janela vazia dobra a seguinte até aqui: acha a primeira vaga distante sem dezenas de chamadas. */
export const JANELA_MAXIMA_DIAS = 112;
export const DIAS_POR_ABA = 3;
export const HORARIOS_INICIAIS = 4;

export const MENSAGEM_LIMITADO = 'Muitos resultados: refine por unidade, médico ou UF.';
/** Mesma frase do back (400): sem isso a varredura seria pela marca toda. */
export const MENSAGEM_FALTA_ESPECIALIDADE_OU_EXAME = 'Escolha uma especialidade ou um exame para buscar.';

export interface AgendaBuscaDisponibilidade {
  tipo: AgendaTipoBusca;
  dtInicio: string;
  cdEspecialidade: number | null;
  /** Só consulta: no exame as agendas dos parceiros já recortam o médico. */
  cdMedico: number | null;
  uf: string | null;
  cdEstabelecimento: number | null;
  soRetorno: boolean;
  /** Exame: as agendas dos parceiros que sobraram na cascata. */
  cdAgendas: number[];
  nrSeqProcInterno: number | null;
  exameEscolhido: boolean;
}

export function fimHorizonte(dtInicio: string): string {
  return somarDias(dtInicio, DIAS_HORIZONTE);
}

export function janelaSeguinte(inicio: string | null, tamanho: number, fim: string): AgendaJanela | null {
  if (!inicio || inicio > fim) return null;
  const dtFim = somarDias(inicio, tamanho - 1);
  return { dtInicio: inicio, dtFim: dtFim > fim ? fim : dtFim };
}

export function tamanhoSeguinte(tamanho: number, vazia: boolean): number {
  return vazia ? Math.min(tamanho * 2, JANELA_MAXIMA_DIAS) : tamanho;
}

/** O que falta para buscar; null = pronto. O back agora recusa busca sem especialidade nem exame. */
export function faltaParaBuscar(b: AgendaBuscaDisponibilidade): string | null {
  if (!b.dtInicio) return 'Informe a data inicial.';
  if (b.tipo === 'EXAME') {
    if (!b.exameEscolhido) return MENSAGEM_FALTA_ESPECIALIDADE_OU_EXAME;
    return b.cdAgendas.length || b.nrSeqProcInterno ? null : 'Nenhuma agenda oferece este exame com esses filtros.';
  }
  return b.cdEspecialidade ? null : MENSAGEM_FALTA_ESPECIALIDADE_OU_EXAME;
}

/**
 * Supervisão com "só com vaga de retorno" não manda o filtro ao back: ele tiraria os dias esgotados,
 * que ela ainda pode agendar. O recorte fica com regraDeRetorno, marcando o cheio.
 */
export function paramsDisponibilidade(b: AgendaBuscaDisponibilidade, j: AgendaJanela, supervisor: boolean): AgendaDisponibilidadeParams {
  const exame = b.tipo === 'EXAME';
  return {
    dtInicio: j.dtInicio,
    dtFim: j.dtFim,
    tipo: b.tipo,
    cdEspecialidade: exame ? null : b.cdEspecialidade,
    cdMedico: exame ? null : b.cdMedico,
    cdEstabelecimento: b.cdEstabelecimento,
    uf: b.uf,
    nrSeqProcInterno: exame ? b.nrSeqProcInterno : null,
    cdAgendas: exame && b.cdAgendas.length ? b.cdAgendas.join(',') : null,
    soRetorno: !exame && b.soRetorno && !supervisor ? 1 : null,
  };
}

export function chaveRetorno(cdAgenda: number, data: string): string {
  return `${cdAgenda}_${data}`;
}

export function retornoDoDia(a: AgendaDisponibilidadeAgenda, data: string): AgendaRetornoDisponivel | null {
  const chave = chaveRetorno(a.cdAgenda, data);
  return a.retornos.find(r => r.chave === chave || (r.cdAgenda === a.cdAgenda && (r.dtAgenda ?? '').slice(0, 10) === data)) ?? null;
}

const dia = (h: AgendaVaga) => (h.dtDisponivel ?? '').slice(0, 10);

/** Refaz contagens e datas depois de tirar horários. */
function recontar(d: AgendaDisponibilidade, unidades: AgendaDisponibilidadeUnidade[]): AgendaDisponibilidade {
  const datas = new Set<string>();
  let total = 0;
  const limpas = unidades
    .map(u => {
      const agendas = u.agendas
        .map(a => ({ ...a, qtVagas: a.horarios.length }))
        .filter(a => a.horarios.length);
      agendas.forEach(a => a.horarios.forEach(h => datas.add(dia(h))));
      const qt = agendas.reduce((n, a) => n + a.qtVagas, 0);
      total += qt;
      return { ...u, agendas, qtVagas: qt };
    })
    .filter(u => u.agendas.length);
  return { ...d, unidades: limpas, datas: [...datas].filter(Boolean).sort(), totalVagas: total };
}

/** Recorte da supervisão: agenda e dia com regra de retorno, esgotados inclusive. */
export function regraDeRetorno(d: AgendaDisponibilidade): AgendaDisponibilidade {
  return recontar(d, d.unidades.map(u => ({
    ...u,
    agendas: u.agendas.map(a => ({
      ...a,
      horarios: a.horarios.filter(h => {
        const r = retornoDoDia(a, dia(h));
        return !!r && r.qtLimite !== null && r.qtLimite !== undefined;
      }),
    })),
  })));
}

/** Junta a janela nova ao que já está na tela (unidade e agenda pelo código). */
export function mesclarDisponibilidade(atual: AgendaDisponibilidade | null, nova: AgendaDisponibilidade): AgendaDisponibilidade {
  if (!atual) return { ...nova, unidades: nova.unidades.map(u => ({ ...u, agendas: u.agendas.map(a => ({ ...a })) })) };
  const unidades = atual.unidades.map(u => ({ ...u, agendas: u.agendas.map(a => ({ ...a })) }));
  for (const u of nova.unidades) {
    const existente = unidades.find(x => x.cdEstabelecimento === u.cdEstabelecimento);
    if (!existente) { unidades.push({ ...u, agendas: u.agendas.map(a => ({ ...a })) }); continue; }
    existente.qtVagas += u.qtVagas;
    for (const a of u.agendas) {
      const ag = existente.agendas.find(x => x.cdAgenda === a.cdAgenda);
      if (!ag) { existente.agendas.push({ ...a }); continue; }
      ag.horarios = [...ag.horarios, ...a.horarios];
      ag.retornos = [...ag.retornos, ...a.retornos];
      ag.qtVagas += a.qtVagas;
    }
  }
  unidades.sort((a, b) => a.cdEstabelecimento - b.cdEstabelecimento);
  return {
    unidades,
    datas: [...new Set([...atual.datas, ...nova.datas])].sort(),
    totalVagas: atual.totalVagas + nova.totalVagas,
    limitado: atual.limitado || nova.limitado,
    retornosVerificados: atual.retornosVerificados && nova.retornosVerificados,
    nrSeqProcInterno: atual.nrSeqProcInterno ?? nova.nrSeqProcInterno,
    cdProcedimento: atual.cdProcedimento ?? nova.cdProcedimento,
    preparo: atual.preparo ?? nova.preparo,
  };
}

/** Horário de hoje que já passou não é oferecido, como no original. */
function oferecivel(h: AgendaVaga, hoje: string, agora: string): boolean {
  const d = dia(h);
  if (!d || d < hoje) return false;
  return d !== hoje || (h.hrInicio ?? '') > agora;
}

export function datasComVaga(d: AgendaDisponibilidade | null, hoje: string, agora: string): string[] {
  if (!d) return [];
  const datas = new Set<string>();
  for (const u of d.unidades) for (const a of u.agendas) for (const h of a.horarios) if (oferecivel(h, hoje, agora)) datas.add(dia(h));
  return [...datas].sort();
}

export interface AgendaRetornoCard {
  texto: string;
  cheio: boolean;
}

export interface AgendaCardAgenda {
  agenda: AgendaDisponibilidadeAgenda;
  horarios: AgendaVaga[];
  retorno: AgendaRetornoCard | null;
}

export interface AgendaCardUnidade {
  unidade: AgendaDisponibilidadeUnidade;
  agendas: AgendaCardAgenda[];
  total: number;
}

export function textoRetorno(r: AgendaRetornoDisponivel | null): AgendaRetornoCard | null {
  if (!r || r.qtLimite === null || r.qtLimite === undefined) return null;
  const cheio = !r.permitido || r.qtAtual >= r.qtLimite;
  return { texto: `Ret: ${r.qtAtual}/${r.qtLimite}${cheio ? ' (cheio)' : ''}`, cheio };
}

export function cardsDoDia(d: AgendaDisponibilidade | null, data: string | null, hoje: string, agora: string): AgendaCardUnidade[] {
  if (!d || !data) return [];
  const cards: AgendaCardUnidade[] = [];
  for (const unidade of d.unidades) {
    const agendas: AgendaCardAgenda[] = [];
    for (const agenda of unidade.agendas) {
      const horarios = agenda.horarios
        .filter(h => dia(h) === data && oferecivel(h, hoje, agora))
        .sort((x, y) => (x.hrInicio ?? '').localeCompare(y.hrInicio ?? ''));
      if (!horarios.length) continue;
      agendas.push({ agenda, horarios, retorno: textoRetorno(retornoDoDia(agenda, data)) });
    }
    if (agendas.length) cards.push({ unidade, agendas, total: agendas.reduce((n, a) => n + a.horarios.length, 0) });
  }
  return cards;
}

export function rotuloIdade(min: number | null, max: number | null): string | null {
  if (min === null && max === null) return null;
  return `${min ?? 0} - ${max ?? '∞'} anos`;
}

// --- Preços -------------------------------------------------------------------------------------

/** null = não dá para cotar (consulta sem especialidade escolhida: cada card seria de uma). */
export function paramsPrecos(
  b: AgendaBuscaDisponibilidade,
  cdEstabelecimento: number,
  d: AgendaDisponibilidade | null,
  parceiros: AgendaExame[],
): AgendaPrecosParams | null {
  if (b.tipo !== 'EXAME') {
    return b.cdEspecialidade ? { tipo: b.tipo, cdEstabelecimento, cdEspecialidade: b.cdEspecialidade } : null;
  }
  // Parceiros com procedimentos diferentes: cada unidade é cotada pelo dela.
  const cdProcedimento = parceiros.find(p => p.cdEstabelecimento === cdEstabelecimento && p.cdProcedimento)?.cdProcedimento
    ?? d?.cdProcedimento ?? parceiros.find(p => p.cdProcedimento)?.cdProcedimento ?? null;
  if (!cdProcedimento && !b.nrSeqProcInterno) return null;
  return { tipo: 'EXAME', cdEstabelecimento, nrSeqProcInterno: b.nrSeqProcInterno, cdProcedimento };
}

// --- Preço do paciente com plano ----------------------------------------------------------------

/** Preço só aparece com paciente escolhido: com plano é o dele, sem plano é o particular. */
export type AgendaModoPreco = 'nenhum' | 'paciente' | 'particular' | 'plano-carregando' | 'plano-erro';

export function modoPreco(temPaciente: boolean, planoPronto: boolean, plano: AgendaPlanoPaciente | null): AgendaModoPreco {
  if (!temPaciente) return 'nenhum';
  if (!planoPronto) return 'plano-carregando';
  if (!plano) return 'plano-erro';
  return plano.temPlano ? 'paciente' : 'particular';
}

export type AgendaPrecoDoPaciente =
  | { estado: 'carregando' }
  | { estado: 'ok'; valor: number; plano: string }
  | { estado: 'sem'; plano: string }
  | { estado: 'erro'; mensagem: string };

/** Mesmos parâmetros do modal de agendamento, com o convênio e a categoria do plano do paciente. */
export function paramsPrecoPaciente(
  b: AgendaBuscaDisponibilidade,
  cdEstabelecimento: number,
  d: AgendaDisponibilidade | null,
  parceiros: AgendaExame[],
  cdPessoaFisica: string,
  plano: AgendaPlanoPaciente,
): AgendaPrecoParams | null {
  const base = paramsPrecos(b, cdEstabelecimento, d, parceiros);
  if (!base || !plano.temPlano) return null;
  const exame = b.tipo === 'EXAME';
  const doParceiro = exame ? parceiros.find(p => p.cdEstabelecimento === cdEstabelecimento && p.nrSeqProcInterno) : undefined;
  return {
    tipo: b.tipo,
    cdPessoaFisica,
    cdEstabelecimento,
    cdConvenio: plano.cdConvenio,
    cdCategoria: plano.cdCategoria,
    cdEspecialidade: exame ? null : b.cdEspecialidade,
    nrSeqProcInterno: exame ? doParceiro?.nrSeqProcInterno ?? b.nrSeqProcInterno : null,
    cdProcedimento: exame ? base.cdProcedimento ?? null : null,
  };
}

export function chavePrecoPaciente(p: AgendaPrecoParams): string {
  return [p.tipo, p.cdPessoaFisica, p.cdEstabelecimento, p.cdConvenio, p.cdCategoria, p.cdEspecialidade, p.nrSeqProcInterno, p.cdProcedimento].join('|');
}

/** null é falha; sem valor no plano é "sem preço" e nunca vira o particular. */
export function precoDoPaciente(p: AgendaPrecoPaciente | null, plano: AgendaPlanoPaciente): AgendaPrecoDoPaciente {
  if (!p) return { estado: 'erro', mensagem: 'Erro ao consultar o preço do paciente nesta unidade.' };
  const nome = plano.dsCategoria || p.planName || plano.dsConvenio || 'plano do paciente';
  if (p.vlPlanoPaciente === null || p.vlPlanoPaciente === undefined) return { estado: 'sem', plano: nome };
  return { estado: 'ok', valor: p.vlPlanoPaciente, plano: nome };
}

/** Paciente sem plano: o preço é o particular da unidade, sem as faixas dos outros planos. */
export function precoParticular(p: AgendaPrecos | null): AgendaPrecoDoPaciente {
  if (!p) return { estado: 'erro', mensagem: 'Erro ao consultar o preço do paciente nesta unidade.' };
  if (p.vlParticular === null || p.vlParticular === undefined) return { estado: 'sem', plano: 'Particular' };
  return { estado: 'ok', valor: p.vlParticular, plano: 'Particular' };
}

/** Exame: o mesmo procedimento em todos os parceiros, senão a busca segue só pelas agendas. */
export function procInternoUnico(parceiros: AgendaExame[]): number | null {
  const unicos = [...new Set(parceiros.map(p => p.nrSeqProcInterno).filter(n => n !== null && n !== undefined))];
  return unicos.length === 1 ? unicos[0] : null;
}

export function preparoDaAgenda(cdAgenda: number, parceiros: AgendaExame[], daResposta: AgendaPreparoExame | null): AgendaPreparoExame | null {
  const p = parceiros.find(x => x.cdAgenda === cdAgenda);
  const pac = p?.dsOrientacaoPac || daResposta?.pac || '';
  const usuario = p?.dsOrientacaoUsuario || daResposta?.usuario || '';
  return pac || usuario ? { pac, usuario } : null;
}

// --- Abrir o novo agendamento no horário ----------------------------------------------------------

export interface AgendaExameInicial {
  nrSeqProcInterno: number;
  cdProcedimento: number | null;
  dsProcExame: string;
}

export interface AgendaHorarioEscolhido {
  linha: AgendaGradeLinha;
  filtro: AgendaFiltroAplicado;
  exame: AgendaExameInicial | null;
  /** Paciente já escolhido na busca: o modal abre com ele. */
  paciente?: AgendaPacienteEscolhido | null;
}

/** O horário livre no formato da grade, que é o que o modal de novo agendamento recebe. */
export function linhaDoHorario(h: AgendaVaga, u: AgendaDisponibilidadeUnidade, a: AgendaDisponibilidadeAgenda, exame: AgendaExameInicial | null): AgendaGradeLinha {
  const medico = { cdMedico: h.cdMedico ?? a.cdMedico, nmMedico: h.nmMedico ?? a.nmMedico };
  return {
    id: String(h.id), tipo: h.tipo ?? a.tipo ?? 'CONSULTA', nrSequencia: h.id, cdAgenda: h.cdAgenda, dtAgenda: dia(h),
    hrInicio: h.hrInicio, nrMinutoDuracao: h.duracao, dsSenha: null, ieStatusAgenda: h.ieStatusAgenda, dsStatusAgenda: null,
    ieClassifAgenda: h.ieClassifAgenda, dsClassifAgenda: null, ieBloqueado: 'N', dsBloqueio: null, iePrioridade: 'N',
    paciente: { cdPessoaFisica: null, nmPaciente: null, nrCpf: null, nrAtendimento: null },
    medico, medicoAgenda: { cdMedico: a.cdMedico, nmMedico: a.nmMedico },
    especialidade: { cdEspecialidade: h.cdEspecialidade ?? a.cdEspecialidade, dsEspecialidade: h.dsEspecialidade ?? a.dsEspecialidade, cdEspecialidadeAgend: null },
    estabelecimento: { cdEstabelecimento: u.cdEstabelecimento, nmEstabelecimento: u.nmEstabelecimento },
    convenio: { cdConvenio: null, dsConvenio: null }, categoria: { cdCategoria: null, dsCategoria: null }, dependente: null,
    dsObservacao: null, dsAgenda: h.dsAgenda ?? a.dsAgenda, nrSeqAreaAtuacao: null,
    cdProcedimento: exame?.cdProcedimento ?? null, nrSeqProcInterno: exame?.nrSeqProcInterno ?? null, dsExame: exame?.dsProcExame ?? null,
    dtNascimento: null, idade: null, dsEmail: null, nrTelefone: null, nmUsuarioOrig: null, dtAgendamento: null, dtConfirmacao: null,
    nmUsuarioConfirm: null, dtFimAtendimento: null, usuarioFimAtend: null, qtMinEspera: null, ieEncaixe: null, ieRetorno: null,
    dtAguardando: null, nmUsuarioAguardando: null, dtConsulta: null, nmUsuarioConsulta: null, dtAtendido: null, nmUsuarioAtendido: null,
    dtCancelamento: null, nmUsuarioCancelamento: null, cdMotivoCancelamento: null, nmUsuarioCopiaTrans: null, dtCopiaTrans: null,
    ieTipoAtendimento: null, dsTipoAtendimento: null, nrSeqTipoConsulta: null,
  };
}

/**
 * Filtro sintético da agenda do horário. A especialidade é a pedida na busca, não a titular: numa
 * agenda mista é ela que o modal pré-seleciona (e que a faixa de preço usou).
 */
export function filtroDoHorario(h: AgendaVaga, u: AgendaDisponibilidadeUnidade, a: AgendaDisponibilidadeAgenda, b: AgendaBuscaDisponibilidade): AgendaFiltroAplicado {
  return {
    tipo: b.tipo, cdEstabelecimento: u.cdEstabelecimento, cdEspecialidade: b.tipo !== 'EXAME' ? b.cdEspecialidade : null,
    cdAgenda: h.cdAgenda, cdMedico: null, ieClassifAgenda: null, data: dia(h),
    cancelados: false, soAgendados: false, soLivres: false,
    agenda: { cdAgenda: a.cdAgenda, dsAgenda: a.dsAgenda ?? '', dsObservacao: null, cdPessoaFisica: null, cdEstabelecimento: u.cdEstabelecimento, ieTipoAtendimento: null },
    automatico: false,
  };
}

export function exameDoHorario(h: AgendaVaga, nome: string, parceiros: AgendaExame[], d: AgendaDisponibilidade | null): AgendaExameInicial | null {
  const p = parceiros.find(x => x.cdAgenda === h.cdAgenda);
  if (p) return { nrSeqProcInterno: p.nrSeqProcInterno, cdProcedimento: p.cdProcedimento, dsProcExame: p.dsProcExame ?? nome };
  if (d?.nrSeqProcInterno) return { nrSeqProcInterno: d.nrSeqProcInterno, cdProcedimento: d.cdProcedimento, dsProcExame: nome };
  return null;
}

export function horarioEscolhido(
  h: AgendaVaga, u: AgendaDisponibilidadeUnidade, a: AgendaDisponibilidadeAgenda, b: AgendaBuscaDisponibilidade,
  exameNome: string, parceiros: AgendaExame[], d: AgendaDisponibilidade | null,
): AgendaHorarioEscolhido {
  const exame = b.tipo === 'EXAME' ? exameDoHorario(h, exameNome, parceiros, d) : null;
  return { linha: linhaDoHorario(h, u, a, exame), filtro: filtroDoHorario(h, u, a, b), exame };
}

// --- Calendário -------------------------------------------------------------------------------

export interface AgendaCelulaCalendario {
  data: string | null;
  numero: number;
  comVaga: boolean;
  passado: boolean;
  hoje: boolean;
}

export function celulasCalendario(mes: string, comVaga: Set<string>, hoje: string): AgendaCelulaCalendario[] {
  const [a, m] = mes.split('-').map(Number);
  const primeiro = new Date(a, m - 1, 1).getDay();
  const dias = new Date(a, m, 0).getDate();
  const celulas: AgendaCelulaCalendario[] = Array.from({ length: primeiro }, () => ({ data: null, numero: 0, comVaga: false, passado: false, hoje: false }));
  for (let d = 1; d <= dias; d++) {
    const data = `${mes}-${String(d).padStart(2, '0')}`;
    celulas.push({ data, numero: d, comVaga: comVaga.has(data), passado: data < hoje, hoje: data === hoje });
  }
  return celulas;
}

export function ultimoDiaDoMes(mes: string): string {
  const [a, m] = mes.split('-').map(Number);
  return `${mes}-${String(new Date(a, m, 0).getDate()).padStart(2, '0')}`;
}
